import Job from 'jobs/Job'
import qs from 'qs'
import { AnswerDTO } from '../dtos/questions'

export interface OwnerScopedInput {
  publicId: string
  workspaceIds: Array<string>
}

/**
 * Used to prefill a new draft.
 */
export class PrefilledDraftInput {
  /**
   * Constructor.
   */
  constructor(
    public legendId: string | undefined,
    public userPublicId: string | undefined,
    public teamId: string | null | undefined,
    public answers: AnswerDTO[],
    public ownerScopedInput: OwnerScopedInput | undefined,
  ) {}

  /**
   * Whether the prefilled draft is empty.
   */
  get isEmpty() {
    const hasAnythingPrefilled =
      this.legendId !== undefined ||
      this.userPublicId !== undefined ||
      this.teamId !== undefined ||
      this.answers.length > 0
    return !hasAnythingPrefilled
  }

  /**
   * Converts the draft input to a query string.
   */
  toQueryString() {
    // May God forgive me.
    const target: any = {
      legend: this.legendId,
      team: this.teamId,
    }

    for (const answer of this.answers) {
      target[`q.${answer.id}`] = answer.answer
    }

    return qs.stringify(target, {
      arrayFormat: 'indices',
      encode: false,
      addQueryPrefix: true,
      skipNulls: false,
    })
  }

  static empty() {
    return new PrefilledDraftInput(
      undefined,
      undefined,
      undefined,
      [],
      undefined,
    )
  }

  /**
   * Parses a prefilled draft input from a query string.
   *
   * @param queryString
   */
  static parse(queryString: string): PrefilledDraftInput {
    const parsed: any = qs.parse(queryString, {
      parseArrays: true,
      parameterLimit: 5000,
      ignoreQueryPrefix: true,
    })
    return new PrefilledDraftInput(
      typeof parsed.legend === 'string' ? parsed.legend : undefined,
      typeof parsed.userPublicId === 'string' ? parsed.userPublicId : undefined,
      'team' in parsed
        ? // Parsed empty string = myself, but we want to represent that as `null`
          String(parsed.team) || null
        : undefined,
      parseAnswersFromQueryObject(parsed),
      typeof parsed.scopedForUserPublicId === 'string'
        ? parsed.scopedForUserPublicId
        : undefined,
    )
  }

  /**
   * Creates a prefilled draft input from a Job.
   *
   * @param job
   */
  static fromJob(job: Job) {
    return new PrefilledDraftInput(
      job.legendVersionId.id,
      job.owner?.user.publicId ?? undefined,
      job.ownerTeam?.id ?? null,
      job.answers,
      undefined,
    )
  }
}

/**
 * Parses answers from the query string.
 *
 * @param parsed
 */
function parseAnswersFromQueryObject(parsed: any) {
  const result: AnswerDTO[] = []
  for (const key in parsed) {
    if (key.startsWith('q.')) {
      result.push({
        id: key.substr(2),
        answer: parsed[key],
      })
    }
  }
  return result
}
